<template>
    <v-row class="ma-0 pb-offset">
        <v-col cols="12" class="pa-0">
            <v-card class="flex pa-0 background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3">Configurazione Rilevamento Duplicati</p>
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider></v-divider>
                
                <v-list three-line subheader class="background-2 pa-2">
                    <v-subheader>Abilitazione</v-subheader>
                    <v-list-item>
                        <v-list-item-action>
                            <v-checkbox 
                                color="primary" 
                                v-model="settings[`${modelPrefix}_duplicate_enabled`]"
                            ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>Abilita Rilevamento Duplicati</v-list-item-title>
                            <v-list-item-subtitle>
                                Controlla automaticamente la presenza di duplicati quando vengono creati o modificati {{ modelLabel }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                    
                    <v-divider v-if="settings[`${modelPrefix}_duplicate_enabled`]"></v-divider>
                    
                    <template v-if="settings[`${modelPrefix}_duplicate_enabled`]">
                        <v-divider></v-divider>

                        <v-subheader>Rilevamento Cross-Model</v-subheader>
                        <v-list-item>
                            <v-list-item-action>
                                <v-checkbox
                                    color="primary"
                                    v-model="settings[`${modelPrefix}_duplicate_cross_model_enabled`]"
                                ></v-checkbox>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>Estendi ad altri modelli</v-list-item-title>
                                <v-list-item-subtitle>
                                    Cerca duplicati anche tra record di altri tipi ({{ $trans('leads') }}, {{ $trans('prospects') }}, {{ $trans('companies') }})
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>

                        <v-list-item v-if="settings[`${modelPrefix}_duplicate_cross_model_enabled`]">
                            <v-list-item-content>
                                <v-autocomplete
                                    v-model="settings[`${modelPrefix}_duplicate_cross_model_types`]"
                                    :items="crossModelTypeOptions"
                                    item-text="text"
                                    item-value="value"
                                    label="Tipi da includere oltre al tipo corrente"
                                    multiple
                                    chips
                                    small-chips
                                    deletable-chips
                                    outlined
                                    dense
                                    :menu-props="{'offset-y': true}"
                                    hide-details="auto"
                                    hint="Seleziona gli altri tipi Business da confrontare"
                                    persistent-hint>
                                </v-autocomplete>
                            </v-list-item-content>
                        </v-list-item>

                        <v-divider></v-divider>

                        <v-subheader>Campi da Confrontare</v-subheader>
                        <v-list-item>
                            <v-list-item-content>
                                <v-autocomplete
                                    v-model="settings[`${modelPrefix}_duplicate_fields`]"
                                    :items="availableFields"
                                    item-text="text"
                                    item-value="value"
                                    label="Seleziona campi per il confronto"
                                    multiple
                                    chips
                                    small-chips
                                    deletable-chips
                                    outlined
                                    dense
                                    :menu-props="{'offset-y': true}"
                                    hide-details="auto"
                                    hint="Seleziona almeno un campo per il rilevamento duplicati"
                                    persistent-hint>
                                </v-autocomplete>
                            </v-list-item-content>
                        </v-list-item>
                        
                        <v-divider></v-divider>
                        
                        <v-subheader>Notifiche</v-subheader>
                        <v-list-item>
                            <v-list-item-content>
                                <v-autocomplete
                                    v-model="settings[`${modelPrefix}_duplicate_notify_users`]"
                                    :items="usersOptions"
                                    item-text="text"
                                    item-value="value"
                                    label="Utenti e Gruppi da notificare"
                                    multiple
                                    chips
                                    small-chips
                                    deletable-chips
                                    outlined
                                    dense
                                    :menu-props="{'offset-y': true}"
                                    hide-details="auto"
                                    :loading="loadingUsers"
                                    hint="Seleziona utenti e gruppi che riceveranno notifiche quando vengono rilevati duplicati"
                                    persistent-hint>
                                </v-autocomplete>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-list>
                
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn 
                        text 
                        class="pa-2 mx-3" 
                        color="primary" 
                        @click.native="saveSettings"
                        :disabled="!isValid">
                        Salva
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import SettingsOptionsMixin from '@/mixins/SettingsOptionsMixin.js'

export default {
    name: 'duplicate-settings',
    mixins: [SettingsOptionsMixin],
    props: {
        modelType: {
            type: String,
            required: true,
            validator: value => ['lead', 'prospect', 'company'].includes(value)
        }
    },
    data() {
        return {
            model: this.modelType, // For the mixin
            settings: {},
            usersOptions: [],
            loadingUsers: false,
            availableFields: [],
            // Mappa centrale: prefisso chiavi settings + codice Business::TYPE_*
            typeMap: {
                lead:     { prefix: 'leads',     code: 2, labelKey: 'lead' },
                prospect: { prefix: 'prospects', code: 3, labelKey: 'prospect' },
                company:  { prefix: 'companies', code: 0, labelKey: 'company' }
            }
        }
    },

    computed: {
        modelPrefix() {
            return this.typeMap[this.modelType].prefix
        },

        modelLabel() {
            return this.$trans(this.typeMap[this.modelType].labelKey)
        },

        crossModelTypeOptions() {
            // Codici della colonna `business` (vedi Business::TYPE_*). Etichette tradotte dal backend
            // (variano per tenant: es. prospects → Contatti, companies → Clienti).
            const all = [
                { value: 2, text: this.$trans('leads') },
                { value: 3, text: this.$trans('prospects') },
                { value: 0, text: this.$trans('companies') }
            ]
            // Escludi il tipo corrente dalle opzioni (già incluso implicitamente)
            const currentTypeCode = this.typeMap[this.modelType].code
            return all.filter(t => t.value !== currentTypeCode)
        },
        
        isValid() {
            if (!this.settings[`${this.modelPrefix}_duplicate_enabled`]) {
                return true
            }
            
            const fields = this.settings[`${this.modelPrefix}_duplicate_fields`]
            const users = this.settings[`${this.modelPrefix}_duplicate_notify_users`]
            
            return fields && fields.length > 0 && users && users.length > 0
        }
    },

    watch: {
        settings: {
            handler(newVal) {
                const key = `${this.modelPrefix}_duplicate_enabled`
                if (newVal[key] && this.usersOptions.length === 0) {
                    this.loadUsersOptions()
                }
            },
            deep: true
        }
    },
    
    methods: {
        loadAvailableFields() {
            // Get available fields from backend for the current model type
            this.axios.get(`model-fields/${this.modelType}`).then(response => {
                this.availableFields = response.data.fields.map(field => {
                    if (field.key) {
                        return { value: field.key, text: field.label }
                    }
                })
            })
        },

        loadUsersOptions() {
            if (this.usersOptions.length > 0) return // già caricati

            this.loadingUsers = true
            this.axios.get('/async-users')
                .then(response => {
                    this.usersOptions = response.data
                    this.loadingUsers = false
                })
                .catch(error => {
                    console.error('Errore nel caricamento utenti:', error)
                    this.loadingUsers = false
                })
        }
    },
    
    created() {
        // Initialize component
        this.loadAvailableFields()
    },
    
    mounted() {
        // Load users if already enabled
        if (this.settings[`${this.modelPrefix}_duplicate_enabled`]) {
            this.loadUsersOptions()
        }
    }
}
</script>